import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import styles from 'assets/style/common/SearchableFilterSelect.module.scss';

export interface FilterSelectOption {
  value?: string | number;
  label?: string;
  isDisabled?: boolean;
}

interface Props<T extends FilterSelectOption> {
  options: T[];
  selectedValues: T[];
  onChange: (values: T[], changed: T) => void;
  placeholder: string;
  multiple?: boolean;
  id?: string;
  className?: string;
  disabled?: boolean;
}

const normalize = (value: string) => value.toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

function SearchableFilterSelect<T extends FilterSelectOption>({
  options, selectedValues, onChange, placeholder, multiple = false, id, className = '', disabled = false
}: Props<T>) {
  const { formatMessage } = useIntl();
  const root = useRef<HTMLDivElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const visibleOptions = useMemo(() => options.filter(option =>
    normalize(String(option.label || '')).includes(normalize(search.trim()))
  ), [options, search]);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [open]);

  useEffect(() => {
    if (open && searchInput.current) searchInput.current.focus();
    if (!open) setSearch('');
  }, [open]);

  const isSelected = (option: T) => selectedValues.some(value => value.value === option.value);
  const selectOption = (option: T) => {
    if (option.isDisabled) return;
    if (multiple) {
      onChange(isSelected(option)
        ? selectedValues.filter(value => value.value !== option.value)
        : [...selectedValues, option], option);
    } else {
      onChange([option], option);
      setOpen(false);
    }
  };
  const selectVisible = () => {
    const additions = visibleOptions.filter(option => !option.isDisabled && !isSelected(option));
    if (additions.length) onChange([...selectedValues, ...additions], additions[0]);
  };
  const deselectVisible = () => {
    const visibleIds = new Set(visibleOptions.map(option => option.value));
    const changed = visibleOptions[0] || selectedValues[0] || options[0];
    if (changed) onChange(selectedValues.filter(value => !visibleIds.has(value.value)), changed);
  };
  const selectedLabel = selectedValues.length === 0 ? placeholder
    : !multiple || selectedValues.length <= 2 ? selectedValues.map(value => value.label || '').join(', ')
      : `${selectedValues.length}${formatMessage({ id: 'ddl.selected.items' })}`;

  return <div ref={root} className={`${styles.filterSelect} ${className}`}>
    <button id={id} type="button" className={`${styles.trigger} ${selectedValues.length ? styles.hasSelection : ''}`}
      aria-expanded={open} aria-haspopup="listbox" disabled={disabled}
      onClick={() => setOpen(!open)}>
      <span className={styles.triggerLabel}>{selectedLabel}</span>
      <span className={styles.indicators} aria-hidden="true"><span className={styles.arrow} /></span>
    </button>
    {open && <div className={styles.menu}>
      <input ref={searchInput} className={styles.search} type="search"
        value={search} onChange={event => setSearch(event.target.value)}
        placeholder={formatMessage({ id: 'filters.search' })}
        aria-label={formatMessage({ id: 'filters.search' })}
        onKeyDown={event => { if (event.key === 'Escape') setOpen(false); }} />
      {multiple && <div className={styles.actions}>
        <button type="button" onClick={selectVisible} disabled={!visibleOptions.some(option => !option.isDisabled && !isSelected(option))}>
          {formatMessage({ id: 'filters.selectAll' })}
        </button>
        <button type="button" onClick={deselectVisible} disabled={!visibleOptions.some(isSelected)}>
          {formatMessage({ id: 'filters.deselectAll' })}
        </button>
      </div>}
      <div className={styles.options} role="listbox" aria-multiselectable={multiple}>
        {visibleOptions.length ? visibleOptions.map(option => <button key={String(option.value)} type="button"
          role="option" aria-selected={isSelected(option)} disabled={option.isDisabled}
          className={styles.option} onClick={() => selectOption(option)}>
          {multiple && <input type="checkbox" readOnly tabIndex={-1} checked={isSelected(option)} />}
          <span>{option.label}</span>
        </button>) : <div className={styles.noResults}>{formatMessage({ id: 'filters.noResults' })}</div>}
      </div>
    </div>}
  </div>;
}

export default SearchableFilterSelect;
